// Animated drawings for the lux-meter guide (LuxGuide.tsx). Plain SVG with CSS animations
// (styles.css, .lux-*). Every animated part's resting position is the step's final state,
// so with "reduce motion" switched on the drawing still shows what to do.
//
// The meter is drawn after the study's kit meter (a handheld lux meter with a swivelling
// white sensor on top, display and buttons on the front): turn the sensor to face away from
// the display, press it flat against the middle of the white screen, read the display.

import type { CSSProperties, ReactNode } from "react";

const INK = "#1d232b";
const PANEL = "#2b3440";
const YELLOW = "#f2c230";
const SKIN = "#f0c9a8";
const SCREEN_OFF = "#dfe6ee";

function origin(x: number, y: number): CSSProperties {
  return { transformBox: "view-box", transformOrigin: `${x}px ${y}px` } as CSSProperties;
}

function Stage({ children, label }: { children: ReactNode; label: string }) {
  return (
    <svg className="lux-scene" viewBox="0 0 640 360" role="img" aria-label={label}>
      <rect x="0" y="0" width="640" height="360" rx="14" fill="#eaf1f8" />
      {children}
    </svg>
  );
}

/** Monitor, front view. `ring`: the grey circle the white screen shows in the middle. */
function MonitorFront({ x, y, w, h, screen = SCREEN_OFF, ring = false, glow = false }: {
  x: number; y: number; w: number; h: number; screen?: string; ring?: boolean; glow?: boolean;
}) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    <g>
      <rect x={cx - 10} y={y + h} width="20" height="34" fill={PANEL} />
      <rect x={cx - 60} y={y + h + 30} width="120" height="10" rx="5" fill={PANEL} />
      <rect x={x} y={y} width={w} height={h} rx="10" fill={PANEL} />
      <rect x={x + 10} y={y + 10} width={w - 20} height={h - 20} rx="3" fill={screen}
        className={glow ? "lux-glow" : undefined} />
      {ring && <circle cx={cx} cy={cy} r={Math.min(w, h) * 0.16} fill="none" stroke="#9aa5b1" strokeWidth="2.5" />}
      {[0, 1, 2].map((i) => <circle key={i} cx={x + w - 30 + i * 9} cy={y + h - 5} r="2.2" fill="#8792a0" />)}
    </g>
  );
}

/** Monitor seen from the side: the screen faces right (towards "you"). */
function MonitorSide({ x, y, h }: { x: number; y: number; h: number }) {
  return (
    <g>
      <rect x={x - 6} y={y + h * 0.55} width="12" height={h * 0.55} fill={PANEL} />
      <rect x={x - 46} y={y + h * 1.08} width="70" height="10" rx="5" fill={PANEL} />
      <rect x={x} y={y} width="16" height={h} rx="5" fill={PANEL} />
      <rect x={x + 14} y={y + 6} width="4" height={h - 12} fill="#ffffff" />
    </g>
  );
}

/** The meter, front view (display and buttons). `lcd`: the number shown; `lit`: switched on. */
function MeterFront({ x, y, s = 1, lcd = "", lit = true, litAnim = false, sensor = true }: {
  x: number; y: number; s?: number; lcd?: string; lit?: boolean; litAnim?: boolean; sensor?: boolean;
}) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      {sensor && <>
        <rect x="38" y="-22" width="14" height="26" fill={PANEL} />
        <circle cx="45" cy="-38" r="24" fill={PANEL} />
        <circle cx="45" cy="-38" r="16" fill="#ffffff" stroke="#c9d1da" strokeWidth="2" />
      </>}
      <rect x="0" y="0" width="90" height="176" rx="16" fill={YELLOW} stroke={INK} strokeWidth="3" />
      <rect x="9" y="11" width="72" height="152" rx="9" fill={PANEL} />
      <rect x="16" y="20" width="58" height="44" rx="4" fill="#5d6b5f" />
      {lit && (
        <g className={litAnim ? "lux-lcd-on" : undefined}>
          <rect x="16" y="20" width="58" height="44" rx="4" fill="#cfeccf" />
          <text x="70" y="50" textAnchor="end" fontFamily="ui-monospace, monospace" fontWeight="700" fontSize="22" fill={INK}>{lcd}</text>
          <text x="70" y="61" textAnchor="end" fontFamily="system-ui" fontWeight="700" fontSize="8" fill={INK}>LUX</text>
        </g>
      )}
      {[["⏻", 20, 82], ["HOLD", 50, 82], ["LUX/FC", 20, 112], ["MAX", 50, 112]].map(([t, bx, by]) => (
        <g key={String(t)}>
          <rect x={Number(bx)} y={Number(by)} width="24" height="20" rx="5" fill={t === "⏻" ? "#c0392b" : "#56616e"} />
          <text x={Number(bx) + 12} y={Number(by) + 13.5} textAnchor="middle" fontSize={t === "⏻" ? 12 : 6.5}
            fontWeight="700" fill="#fff" fontFamily="system-ui">{t}</text>
        </g>
      ))}
      <text x="45" y="152" textAnchor="middle" fontSize="8" fill="#c9d1da" fontFamily="system-ui">LIGHT METER</text>
    </g>
  );
}

/** The meter from the side: display side on the right ("you"), sensor head on top. The
 *  head turns so the white dome faces left (the screen). */
function MeterSide({ x, y, rotate = false, domeLeft = true }: { x: number; y: number; rotate?: boolean; domeLeft?: boolean }) {
  const hx = x + 17;
  const hy = y - 36;
  return (
    <g>
      <rect x={x} y={y} width="34" height="176" rx="12" fill={YELLOW} stroke={INK} strokeWidth="3" />
      <rect x={x + 26} y={y + 12} width="6" height="150" rx="3" fill={PANEL} />
      <rect x={x + 10} y={y - 20} width="14" height="24" fill={PANEL} />
      <g className={rotate ? "lux-head-turn" : undefined} style={origin(hx, hy)}>
        <circle cx={hx} cy={hy} r="24" fill={PANEL} />
        {domeLeft
          ? <path d={`M ${hx - 6} ${hy - 16} A 16 16 0 0 0 ${hx - 6} ${hy + 16} Z`} fill="#fff" stroke="#c9d1da" strokeWidth="2" transform={`translate(-16 0)`} />
          : <path d={`M ${hx - 16} ${hy - 6} A 16 16 0 0 1 ${hx + 16} ${hy - 6} Z`} fill="#fff" stroke="#c9d1da" strokeWidth="2" transform="translate(0 -16)" />}
      </g>
    </g>
  );
}

function You({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <path d={`M ${x - 26} ${y} Q ${x} ${y - 20} ${x + 26} ${y} Q ${x} ${y + 20} ${x - 26} ${y} Z`} fill="#fff" stroke={INK} strokeWidth="3" />
      <circle cx={x - 4} cy={y} r="8" fill={INK} />
      <text x={x} y={y + 40} textAnchor="middle" fontSize="18" fontWeight="700" fill={INK} fontFamily="system-ui">you</text>
    </g>
  );
}

function Clock({ x, y, r, label }: { x: number; y: number; r: number; label?: string }) {
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill="#fff" stroke={INK} strokeWidth="4" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * Math.PI) / 6;
        return <line key={i} x1={x + Math.sin(a) * r * 0.78} y1={y - Math.cos(a) * r * 0.78}
          x2={x + Math.sin(a) * r * 0.9} y2={y - Math.cos(a) * r * 0.9} stroke={INK} strokeWidth="3" />;
      })}
      <line x1={x} y1={y} x2={x} y2={y - r * 0.5} stroke={INK} strokeWidth="5" strokeLinecap="round" />
      <line className="lux-clock-hand" style={origin(x, y)} x1={x} y1={y} x2={x} y2={y - r * 0.78}
        stroke="#c0392b" strokeWidth="4" strokeLinecap="round" />
      <circle cx={x} cy={y} r="5" fill={INK} />
      {label && <text x={x} y={y + r + 30} textAnchor="middle" fontSize="22" fontWeight="700" fill={INK} fontFamily="system-ui">{label}</text>}
    </g>
  );
}

function Finger({ x, y }: { x: number; y: number }) {
  return (
    <g className="lux-tap">
      <path d={`M ${x} ${y} q -9 0 -9 10 l 0 46 q 0 14 14 14 l 22 0 q 14 0 14 -14 l 0 -26 q 0 -8 -8 -8 q -8 0 -8 8
        q 0 -8 -8 -8 q -7 0 -7 6 l 0 -18 q 0 -10 -10 -10 z`} fill={SKIN} stroke={INK} strokeWidth="2.5"
        transform={`translate(0 0)`} />
    </g>
  );
}

// ------------------------------------------------------------------ scenes ---------------

export function SceneNeed() {
  return (
    <Stage label="A monitor and the light meter">
      <MonitorFront x={50} y={58} w={300} h={180} />
      <MeterFront x={440} y={120} lit={false} />
      <text x="200" y="330" textAnchor="middle" fontSize="20" fill={INK} fontFamily="system-ui">your monitor</text>
      <text x="485" y="330" textAnchor="middle" fontSize="20" fill={INK} fontFamily="system-ui">the light meter</text>
    </Stage>
  );
}

export function SceneWarm() {
  return (
    <Stage label="Let the monitor warm up for 15 minutes">
      <MonitorFront x={40} y={60} w={330} h={190} screen="#f4f7fb" glow />
      <Clock x={500} y={140} r={78} label="15 minutes" />
    </Stage>
  );
}

export function SceneSettings() {
  return (
    <Stage label="Keep the monitor settings and room lights the same">
      <MonitorFront x={40} y={60} w={330} h={190} screen="#f4f7fb" />
      <circle cx={340} cy={245} r="24" fill="none" stroke="#c0392b" strokeWidth="4" className="lux-pulse" style={origin(340, 245)} />
      <g transform="translate(470 70)">
        <path d="M 0 0 L 80 0 L 60 50 L 20 50 Z" fill="#ffe08a" stroke={INK} strokeWidth="3" />
        <rect x="36" y="50" width="8" height="120" fill={PANEL} />
        <rect x="10" y="170" width="60" height="10" rx="5" fill={PANEL} />
      </g>
      <text x="205" y="330" textAnchor="middle" fontSize="19" fill={INK} fontFamily="system-ui">monitor buttons: leave as they are</text>
      <text x="510" y="330" textAnchor="middle" fontSize="19" fill={INK} fontFamily="system-ui">lights as usual</text>
    </Stage>
  );
}

export function ScenePower() {
  return (
    <Stage label="Press the power button; the display shows a number and LUX">
      <MeterFront x={250} y={70} s={1.45} lcd="0" litAnim />
      <Finger x={300} y={200} />
      <text x="470" y="120" fontSize="20" fill={INK} fontFamily="system-ui">display says</text>
      <text x="470" y="148" fontSize="26" fontWeight="700" fill={INK} fontFamily="system-ui">LUX</text>
      <text x="470" y="176" fontSize="18" fill="#5d6875" fontFamily="system-ui">(not FC)</text>
    </Stage>
  );
}

export function SceneRotate() {
  return (
    <Stage label="Turn the white sensor so it points away from the display">
      <defs>
        <marker id="luxArrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#2a6fdb" />
        </marker>
      </defs>
      <MeterSide x={300} y={140} rotate />
      <path d="M 250 104 L 150 104" fill="none" stroke="#2a6fdb" strokeWidth="6" markerEnd="url(#luxArrow)" className="lux-fade" />
      <text x="150" y="150" textAnchor="middle" fontSize="20" fontWeight="700" fill={INK} fontFamily="system-ui">white sensor</text>
      <text x="150" y="176" textAnchor="middle" fontSize="20" fill={INK} fontFamily="system-ui">points this way</text>
      <text x="150" y="200" textAnchor="middle" fontSize="16" fill="#5d6875" fontFamily="system-ui">(towards the screen)</text>
      <path d="M 350 230 L 440 230" fill="none" stroke="#2a6fdb" strokeWidth="6" markerEnd="url(#luxArrow)" className="lux-fade" />
      <You x={510} y={230} />
      <text x="510" y="150" textAnchor="middle" fontSize="20" fontWeight="700" fill={INK} fontFamily="system-ui">display faces you</text>
    </Stage>
  );
}

/** Where (front view) and how (side view): flat against the middle, display facing you. */
// The drawings never show a sample number (it could be copied instead of the real reading).
const NO_NUMBER = "- - -";

export function ScenePress({ lcd = NO_NUMBER }: { lcd?: string }) {
  return (
    <Stage label="Press the sensor flat against the middle of the white screen and read the number">
      <line x1="320" y1="20" x2="320" y2="340" stroke="#c6d3e1" strokeWidth="2" />
      <text x="160" y="40" textAnchor="middle" fontSize="18" fontWeight="700" fill={INK} fontFamily="system-ui">where: the middle</text>
      <MonitorFront x={30} y={70} w={260} h={160} screen="#ffffff" ring />
      <g className="lux-slide-in">
        <circle cx="160" cy="150" r="17" fill="none" stroke="#2a6fdb" strokeWidth="4" strokeDasharray="6 4" />
      </g>
      <text x="480" y="40" textAnchor="middle" fontSize="18" fontWeight="700" fill={INK} fontFamily="system-ui">how: flat, display to you</text>
      <MonitorSide x={350} y={70} h={180} />
      <g className="lux-press">
        <MeterSide x={389} y={170} />
        <g transform="translate(436 96)">
          <rect x="0" y="0" width="92" height="56" rx="8" fill="#cfeccf" stroke={INK} strokeWidth="3" />
          <text x="84" y="35" textAnchor="end" fontFamily="ui-monospace, monospace" fontWeight="700" fontSize="28" fill={INK}>{lcd}</text>
          <text x="84" y="50" textAnchor="end" fontSize="10" fontWeight="700" fill={INK} fontFamily="system-ui">LUX</text>
        </g>
      </g>
      <You x={590} y={300} />
    </Stage>
  );
}

export function SceneType({ lcd = NO_NUMBER }: { lcd?: string }) {
  return (
    <Stage label="Type the number the meter showed">
      <MeterFront x={90} y={90} s={1.25} lcd={lcd} sensor={false} />
      <path d="M 250 190 L 360 190" stroke="#2a6fdb" strokeWidth="6" markerEnd="url(#luxArrow2)" />
      <defs>
        <marker id="luxArrow2" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#2a6fdb" />
        </marker>
      </defs>
      <rect x="380" y="160" width="190" height="64" rx="10" fill="#fff" stroke={INK} strokeWidth="3" />
      <clipPath id="luxTypeClip"><rect className="lux-type" x="390" y="165" width="120" height="54" /></clipPath>
      <text x="400" y="204" fontSize="34" fontWeight="700" fill={INK} fontFamily="ui-monospace, monospace" clipPath="url(#luxTypeClip)">{lcd}</text>
      <text x="520" y="202" fontSize="20" fill="#5d6875" fontFamily="system-ui">lux</text>
    </Stage>
  );
}

export function SceneDone({ ok = true }: { ok?: boolean }) {
  return (
    <Stage label={ok ? "Done" : "Adjust and measure again"}>
      <MonitorFront x={40} y={60} w={330} h={190} screen="#ffffff" />
      {ok ? (
        <g transform="translate(500 160)">
          <circle r="70" fill="#1f7a3a" />
          <path d="M -32 2 L -8 26 L 36 -24" fill="none" stroke="#fff" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ) : (
        <g transform="translate(500 160)">
          <circle r="70" fill="#b25b00" />
          <text x="0" y="24" textAnchor="middle" fontSize="80" fontWeight="700" fill="#fff" fontFamily="system-ui">!</text>
        </g>
      )}
    </Stage>
  );
}
